<template>
    <div v-if="$store.state.isModalOpenTwo" class="modal fade show ws-modal" aria-labelledby="addNewCardTitle">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" ref="modalRef">
            <div class="modal-content">
                <div class="ws-header">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                        <div class="min-width-0">
                            <h1 class="ws-title mb-0">Work Status Timesheet</h1>
                            <p v-if="taskTitle" class="ws-subtitle mb-0">{{ taskTitle }}</p>
                        </div>
                        <button
                            @click="onClickClose"
                            type="button"
                            class="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        ></button>
                    </div>

                    <div v-if="hasRows" class="ws-summary">
                        <span
                            v-for="chip in summaryChips"
                            :key="chip.key"
                            class="ws-chip"
                            :style="chipStyle(chip.key)"
                        >
                            {{ chip.label }} <strong>{{ chip.count }}</strong>
                        </span>
                    </div>
                </div>

                <div class="ws-body">
                    <div v-if="! hasRows" class="ws-empty">
                        <i class="fa fa-clipboard-list ws-empty-icon"></i>
                        <p class="mb-0">No work status submitted yet.</p>
                    </div>

                    <div v-if="hasRows" class="d-none d-md-block">
                        <table class="table ws-table mb-0">
                            <thead>
                            <tr>
                                <th>Employee</th>
                                <th>Description</th>
                                <th class="text-end">Hours</th>
                                <th>Location</th>
                                <th class="text-end">Conveyance</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th v-if="canApprove" class="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <template v-for="item in workStatusTimeSheet" :key="item.id">
                                <tr>
                                    <td>
                                        <div class="d-flex align-items-center gap-1">
                                            <span class="ws-avatar">{{ initialOf(item.employee_name) }}</span>
                                            <div class="min-width-0">
                                                <div class="ws-name">{{ item.employee_name }}</div>
                                                <span class="ws-role">{{ roleLabel(item.field_key) }}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="ws-desc" :title="item.description">{{ item.description }}</span>
                                    </td>
                                    <td class="text-end">{{ item.hours }}</td>
                                    <td>{{ item.area_name }}</td>
                                    <td class="text-end">{{ item.conveyance }}</td>
                                    <td class="ws-nowrap">{{ item.date }}</td>
                                    <td>
                                        <span class="ws-pill" :style="pillStyle(item.status)">{{ item.status }}</span>
                                        <button
                                            v-if="hasHistory(item)"
                                            class="btn btn-link btn-sm ws-link"
                                            @click="toggleHistory(item.id)"
                                        >
                                            <i class="fa fa-clock-rotate-left"></i> History
                                        </button>
                                    </td>
                                    <td v-if="canApprove" class="text-center ws-nowrap">
                                        <div v-if="isAwaitingEmployee(item)" class="ws-locked" title="Waiting for the employee to resubmit">
                                            <i class="fa fa-hourglass-half"></i>
                                        </div>
                                        <div v-if="isFinalised(item)" class="ws-locked" title="Already reviewed">
                                            <i class="fa fa-lock"></i>
                                        </div>
                                        <div v-if="! isRowLocked(item)" class="btn-group btn-group-sm">
                                            <button
                                                class="btn btn-outline-success"
                                                title="Approve"
                                                :disabled="loading"
                                                @click="onApprove(item)"
                                            >
                                                <i class="fa fa-thumbs-up"></i>
                                            </button>
                                            <button
                                                class="btn btn-outline-warning"
                                                title="Return"
                                                :disabled="loading"
                                                @click="openCommentFor(item, 'returned')"
                                            >
                                                <i class="fa fa-reply"></i>
                                            </button>
                                            <button
                                                class="btn btn-outline-danger"
                                                title="Reject"
                                                :disabled="loading"
                                                @click="openCommentFor(item, 'rejected')"
                                            >
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>

                                <tr v-if="hasComment(item)" class="ws-sub-row">
                                    <td :colspan="columnCount">
                                        <div class="ws-callout" :style="calloutStyle(item.status)">
                                            <strong class="text-capitalize">{{ item.last_comment.status }}</strong>
                                            <span class="ws-meta">
                                                by {{ item.last_comment.updated_by_name }} · {{ item.last_comment.logged_at }}
                                            </span>
                                            <div>{{ item.last_comment.comment }}</div>
                                        </div>
                                    </td>
                                </tr>

                                <tr v-if="isCommentOpen(item.id)" class="ws-sub-row">
                                    <td :colspan="columnCount">
                                        <div class="ws-comment-box">
                                            <label class="form-label ws-comment-label">
                                                {{ commentLabel }} <span class="text-danger">*</span>
                                            </label>
                                            <textarea
                                                class="form-control ws-comment-input"
                                                rows="2"
                                                v-model="commentText"
                                                :placeholder="commentPlaceholder"
                                            ></textarea>
                                            <small v-if="commentError" class="text-danger">{{ commentError }}</small>
                                            <div class="d-flex justify-content-end gap-1 mt-1">
                                                <button class="btn btn-sm btn-outline-secondary" @click="closeComment">
                                                    Cancel
                                                </button>
                                                <button
                                                    class="btn btn-sm"
                                                    :class="confirmClass"
                                                    :disabled="loading"
                                                    @click="submitComment"
                                                >
                                                    {{ confirmLabel }}
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>

                                <tr v-if="isHistoryOpen(item.id)" class="ws-sub-row">
                                    <td :colspan="columnCount">
                                        <div class="ws-timeline">
                                            <div v-for="log in item.histories" :key="log.id" class="ws-timeline-item">
                                                <span class="ws-pill" :style="pillStyle(log.status)">{{ log.status }}</span>
                                                <span class="ws-meta">
                                                    {{ log.updated_by_name }} · {{ log.logged_at }}
                                                </span>
                                                <div v-if="log.comment" class="ws-timeline-comment">{{ log.comment }}</div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="hasRows" class="d-md-none">
                        <div v-for="item in workStatusTimeSheet" :key="item.id" class="ws-card">
                            <div class="d-flex justify-content-between align-items-start gap-1">
                                <div class="d-flex align-items-center gap-1 min-width-0">
                                    <span class="ws-avatar">{{ initialOf(item.employee_name) }}</span>
                                    <div class="min-width-0">
                                        <div class="ws-name">{{ item.employee_name }}</div>
                                        <span class="ws-role">{{ roleLabel(item.field_key) }}</span>
                                    </div>
                                </div>
                                <span class="ws-pill" :style="pillStyle(item.status)">{{ item.status }}</span>
                            </div>

                            <div class="ws-card-grid">
                                <div>
                                    <span class="ws-card-label">Hours</span>
                                    <span>{{ item.hours }}</span>
                                </div>
                                <div>
                                    <span class="ws-card-label">Date</span>
                                    <span>{{ item.date }}</span>
                                </div>
                                <div>
                                    <span class="ws-card-label">Location</span>
                                    <span>{{ item.area_name }}</span>
                                </div>
                                <div>
                                    <span class="ws-card-label">Conveyance</span>
                                    <span>{{ item.conveyance }}</span>
                                </div>
                            </div>

                            <p v-if="item.description" class="ws-card-desc mb-0">{{ item.description }}</p>

                            <div v-if="hasComment(item)" class="ws-callout" :style="calloutStyle(item.status)">
                                <strong class="text-capitalize">{{ item.last_comment.status }}</strong>
                                <span class="ws-meta">by {{ item.last_comment.updated_by_name }}</span>
                                <div>{{ item.last_comment.comment }}</div>
                            </div>

                            <button
                                v-if="hasHistory(item)"
                                class="btn btn-link btn-sm ws-link p-0"
                                @click="toggleHistory(item.id)"
                            >
                                <i class="fa fa-clock-rotate-left"></i> History
                            </button>

                            <div v-if="isHistoryOpen(item.id)" class="ws-timeline">
                                <div v-for="log in item.histories" :key="log.id" class="ws-timeline-item">
                                    <span class="ws-pill" :style="pillStyle(log.status)">{{ log.status }}</span>
                                    <span class="ws-meta">{{ log.updated_by_name }} · {{ log.logged_at }}</span>
                                    <div v-if="log.comment" class="ws-timeline-comment">{{ log.comment }}</div>
                                </div>
                            </div>

                            <div v-if="isCommentOpen(item.id)" class="ws-comment-box">
                                <label class="form-label ws-comment-label">
                                    {{ commentLabel }} <span class="text-danger">*</span>
                                </label>
                                <textarea
                                    class="form-control ws-comment-input"
                                    rows="2"
                                    v-model="commentText"
                                    :placeholder="commentPlaceholder"
                                ></textarea>
                                <small v-if="commentError" class="text-danger">{{ commentError }}</small>
                                <div class="d-flex justify-content-end gap-1 mt-1">
                                    <button class="btn btn-sm btn-outline-secondary" @click="closeComment">Cancel</button>
                                    <button
                                        class="btn btn-sm"
                                        :class="confirmClass"
                                        :disabled="loading"
                                        @click="submitComment"
                                    >
                                        {{ confirmLabel }}
                                    </button>
                                </div>
                            </div>

                            <div v-if="canApprove && isAwaitingEmployee(item)" class="ws-meta mt-1">
                                <i class="fa fa-hourglass-half"></i> Waiting for the employee to resubmit
                            </div>

                            <div v-if="canApprove && ! isRowLocked(item)" class="btn-group btn-group-sm w-100 mt-1">
                                <button class="btn btn-outline-success" :disabled="loading" @click="onApprove(item)">
                                    <i class="fa fa-thumbs-up"></i> Approve
                                </button>
                                <button
                                    class="btn btn-outline-warning"
                                    :disabled="loading"
                                    @click="openCommentFor(item, 'returned')"
                                >
                                    <i class="fa fa-reply"></i> Return
                                </button>
                                <button
                                    class="btn btn-outline-danger"
                                    :disabled="loading"
                                    @click="openCommentFor(item, 'rejected')"
                                >
                                    <i class="fa fa-times"></i> Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ws-footer">
                    <div>
                        <button
                            v-if="canApprove && pendingCount > 0"
                            class="btn btn-sm btn-success"
                            :disabled="loading"
                            @click="onApproveAll"
                        >
                            <i class="fa fa-thumbs-up"></i> Approve All ({{ pendingCount }})
                        </button>
                        <span v-if="canApprove && pendingCount < 1" class="ws-meta">
                            <i class="fa fa-check"></i> All work status reviewed
                        </span>
                    </div>

                    <button class="btn btn-sm btn-outline-secondary" @click="onClickClose">Close</button>
                </div>
            </div>
        </div>
        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import Loader from "@/components/atom/LoaderComponent";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { getTaskStatusColor } from "@/services/utils/global";

const props = defineProps({
  workStatusTimeSheet: {
    type: Array,
    default: () => []
  },
  canApprove: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  taskTitle: {
    type: String,
    default: ''
  },
});

const $store = useStore();
const modalRef = ref(null);
const loader = ref(false);
const emit = defineEmits(['handleApproveAll', 'handleRowAction']);

const roleLabels = {
  assigned_to: 'Assigned',
  report_to: 'Report To',
  participant_id: 'Participant'
}

const confirmLabelMap = {
  returned: 'Confirm Return',
  rejected: 'Confirm Reject'
}

const confirmClassMap = {
  returned: 'btn-warning',
  rejected: 'btn-danger'
}

const commentLabelMap = {
  returned: 'Return reason',
  rejected: 'Rejection reason'
}

const placeholderMap = {
  returned: 'Explain what needs to be corrected',
  rejected: 'Explain why this work status is rejected'
}

const openCommentId = ref(null);
const commentAction = ref('returned');
const commentText = ref('');
const commentError = ref('');
const openHistoryIds = ref([]);

const hasRows = computed(() => props.workStatusTimeSheet.length > 0);

const columnCount = computed(() => {
  if (props.canApprove) return 8
  return 7
});

const confirmLabel = computed(() => confirmLabelMap[commentAction.value]);
const confirmClass = computed(() => confirmClassMap[commentAction.value]);
const commentLabel = computed(() => commentLabelMap[commentAction.value]);
const commentPlaceholder = computed(() => placeholderMap[commentAction.value]);

const countByStatus = (status) => {
  return props.workStatusTimeSheet.filter(item => item.status === status).length
}

const pendingCount = computed(() => countByStatus('active'));

const summaryChips = computed(() => {
  return [
    { key: 'active', label: 'Pending', count: countByStatus('active') },
    { key: 'approved', label: 'Approved', count: countByStatus('approved') },
    { key: 'returned', label: 'Returned', count: countByStatus('returned') },
    { key: 'rejected', label: 'Rejected', count: countByStatus('rejected') }
  ]
});

const pillStyle = (status) => {
  const color = getTaskStatusColor(status)
  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`
  }
}

const chipStyle = (status) => pillStyle(status)

const calloutStyle = (status) => {
  const color = getTaskStatusColor(status)
  return {
    backgroundColor: color.bg,
    color: color.text,
    borderLeft: `3px solid ${color.border}`
  }
}

const roleLabel = (fieldKey) => {
  const label = roleLabels[fieldKey]
  if (! label) return ''
  return label
}

const initialOf = (name) => {
  if (! name) return '?'
  return name.charAt(0).toUpperCase()
}

const isRowLocked = (item) => {
  return item.status !== 'active'
}

const isAwaitingEmployee = (item) => {
  return item.status === 'returned'
}

const isFinalised = (item) => {
  return ['approved', 'rejected'].includes(item.status)
}

const hasComment = (item) => {
  if (! item.last_comment) return false
  return ! ! item.last_comment.comment
}

const hasHistory = (item) => {
  if (! item.histories) return false
  return item.histories.length > 0
}

const isHistoryOpen = (id) => openHistoryIds.value.includes(id)

const toggleHistory = (id) => {
  if (isHistoryOpen(id)) {
    openHistoryIds.value = openHistoryIds.value.filter(openId => openId !== id)
    return
  }
  openHistoryIds.value = openHistoryIds.value.concat([id])
}

const isCommentOpen = (id) => openCommentId.value === id

const confirmActionMessages = {
  approved: 'approve',
  returned: 'return',
  rejected: 'reject'
}

const isActionConfirmed = (status, employeeName) => {
  const action = confirmActionMessages[status]

  if (status === 'rejected') {
    return window.confirm(`Reject the work status of ${employeeName}? Once rejected they can no longer update it for this task.`)
  }

  return window.confirm(`Are you sure you want to ${action} the work status of ${employeeName}?`)
}

const openCommentFor = (item, action) => {
  openCommentId.value = item.id
  commentAction.value = action
  commentText.value = ''
  commentError.value = ''
}

const closeComment = () => {
  openCommentId.value = null
  commentText.value = ''
  commentError.value = ''
}

const submitComment = () => {
  const trimmed = commentText.value.trim()

  if (! trimmed) {
    commentError.value = 'A comment is required.'
    return
  }

  const row = props.workStatusTimeSheet.find(item => item.id === openCommentId.value)

  if (! row) return

  if (! isActionConfirmed(commentAction.value, row.employee_name)) return

  emit('handleRowAction', {
    id: row.id,
    status: commentAction.value,
    comment: trimmed,
    employee_name: row.employee_name
  })

  closeComment()
}

const onApproveAll = () => {
  const count = pendingCount.value

  if (! window.confirm(`Approve ${count} pending work status? Returned and rejected records will not be changed.`)) return

  closeComment()
  emit('handleApproveAll')
}

const onApprove = (item) => {
  if (! isActionConfirmed('approved', item.employee_name)) return

  closeComment()
  emit('handleRowAction', {
    id: item.id,
    status: 'approved',
    comment: null,
    employee_name: item.employee_name
  })
}

const onClickClose = () => {
    $store.state.isModalOpenTwo = false;
}

</script>

<style scoped>
.ws-modal {
  display: block;
  background: #1c1d1c33;
  z-index: 1060;
}
.min-width-0 {
  min-width: 0;
}
.ws-header {
  padding: 0.75rem 1rem 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}
.ws-title {
  font-size: 1.2rem;
  font-weight: 600;
}
.ws-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
}
.ws-chip {
  padding: 2px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}
.ws-body {
  padding: 0.5rem 1rem;
  min-height: 260px;
}
.ws-table {
  font-size: 0.82rem;
}
.ws-table thead th {
  background: #f9fafb;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6b7280;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}
.ws-table tbody td {
  vertical-align: middle;
  border-bottom: 1px solid #f1f3f5;
}
.ws-sub-row td {
  padding-top: 0;
  border-bottom: 1px solid #f1f3f5;
}
.ws-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #312e81;
  font-weight: 600;
  font-size: 0.78rem;
}
.ws-name {
  font-weight: 600;
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-role {
  font-size: 0.68rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.ws-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 280px;
}
.ws-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}
.ws-nowrap {
  white-space: nowrap;
}
.ws-link {
  font-size: 0.7rem;
  text-decoration: none;
  padding: 0 0 0 6px;
}
.ws-locked {
  color: #9ca3af;
  font-size: 0.8rem;
}
.ws-callout {
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.78rem;
  margin-bottom: 0.5rem;
}
.ws-meta {
  font-size: 0.7rem;
  opacity: 0.8;
  margin-left: 6px;
}
.ws-comment-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem;
  margin-bottom: 0.5rem;
}
.ws-comment-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #374151;
  margin-bottom: 0.25rem;
}
.ws-comment-input {
  resize: none;
  font-size: 0.82rem;
}
.ws-timeline {
  padding: 0.4rem 0 0.6rem;
}
.ws-timeline-item {
  padding: 4px 0 4px 10px;
  border-left: 2px solid #e5e7eb;
  margin-bottom: 4px;
  font-size: 0.78rem;
}
.ws-timeline-comment {
  color: #4b5563;
  font-size: 0.76rem;
}
.ws-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #9ca3af;
}
.ws-empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}
.ws-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.6rem;
}
.ws-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 0.75rem;
  margin: 0.6rem 0;
  font-size: 0.8rem;
}
.ws-card-label {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  color: #6b7280;
}
.ws-card-desc {
  font-size: 0.8rem;
  color: #374151;
  margin-bottom: 0.5rem;
}
.ws-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-top: 1px solid #e5e7eb;
}
</style>
